'use client';

import { c as _c } from "react/compiler-runtime";
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { cn } from 'cn';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export const Select = SelectPrimitive.Root;
type SelectGroupProps = SelectPrimitive.Group.Props;
export function SelectGroup(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("scroll-my-1 p-1", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <SelectPrimitive.Group data-slot="select-group" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type SelectValueProps = SelectPrimitive.Value.Props;
export function SelectValue(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex flex-1 text-left", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <SelectPrimitive.Value data-slot="select-value" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type SelectTriggerProps = {
  size?: 'sm' | 'default';
} & SelectPrimitive.Trigger.Props;
export function SelectTrigger(props) {
  const $ = _c(13);
  let children;
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      size: t0,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
    $[4] = t0;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
    t0 = $[4];
  }
  const size = t0 === undefined ? "default" : t0;
  let t1;
  if ($[5] !== className) {
    t1 = cn("flex w-fit items-center justify-between gap-1.5 rounded-4xl border border-input bg-input/30 px-3 py-2 text-sm whitespace-nowrap transition-colors outline-none", "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50", "disabled:cursor-not-allowed disabled:opacity-50", "aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20", "data-placeholder:text-muted-foreground", "data-[size=default]:h-9", "data-[size=sm]:h-8", "*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5", "dark:hover:bg-input/50", "dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40", "[&_svg]:pointer-events-none [&_svg]:shrink-0", "[&_svg:not([class*='size-'])]:size-4", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectPrimitive.Icon render={<ChevronDownIcon className="pointer-events-none size-4 text-muted-foreground" />} />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== rest || $[10] !== size || $[11] !== t1) {
    t3 = <SelectPrimitive.Trigger data-slot="select-trigger" data-size={size} className={t1} {...rest}>{children}{t2}</SelectPrimitive.Trigger>;
    $[8] = children;
    $[9] = rest;
    $[10] = size;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
type SelectContentProps = SelectPrimitive.Popup.Props & Pick<SelectPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset' | 'alignItemWithTrigger'>;
export function SelectContent(props) {
  const $ = _c(27);
  let children;
  let className;
  let rest;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== props) {
    ({
      className,
      children,
      side: t0,
      sideOffset: t1,
      align: t2,
      alignOffset: t3,
      alignItemWithTrigger: t4,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
    t0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  const side = t0 === undefined ? "bottom" : t0;
  const sideOffset = t1 === undefined ? 4 : t1;
  const align = t2 === undefined ? "center" : t2;
  const alignOffset = t3 === undefined ? 0 : t3;
  const alignItemWithTrigger = t4 === undefined ? true : t4;
  let t5;
  if ($[9] !== className) {
    t5 = cn("relative isolate z-50 max-h-(--available-height) w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-2xl bg-popover text-popover-foreground shadow-2xl ring-1 ring-foreground/5 duration-100", "data-[align-trigger=true]:animate-none", "data-[side=bottom]:slide-in-from-top-2", "data-[side=inline-end]:slide-in-from-left-2", "data-[side=inline-start]:slide-in-from-right-2", "data-[side=left]:slide-in-from-right-2", "data-[side=right]:slide-in-from-left-2", "data-[side=top]:slide-in-from-bottom-2", "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95", "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95", className);
    $[9] = className;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SelectScrollUpButton />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children) {
    t7 = <SelectPrimitive.List>{children}</SelectPrimitive.List>;
    $[12] = children;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SelectScrollDownButton />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== alignItemWithTrigger || $[16] !== rest || $[17] !== t5 || $[18] !== t7) {
    t9 = <SelectPrimitive.Popup data-slot="select-content" data-align-trigger={alignItemWithTrigger} className={t5} {...rest}>{t6}{t7}{t8}</SelectPrimitive.Popup>;
    $[15] = alignItemWithTrigger;
    $[16] = rest;
    $[17] = t5;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== align || $[21] !== alignItemWithTrigger || $[22] !== alignOffset || $[23] !== side || $[24] !== sideOffset || $[25] !== t9) {
    t10 = <SelectPrimitive.Portal><SelectPrimitive.Positioner side={side} sideOffset={sideOffset} align={align} alignOffset={alignOffset} alignItemWithTrigger={alignItemWithTrigger} className="isolate z-50">{t9}</SelectPrimitive.Positioner></SelectPrimitive.Portal>;
    $[20] = align;
    $[21] = alignItemWithTrigger;
    $[22] = alignOffset;
    $[23] = side;
    $[24] = sideOffset;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
type SelectLabelProps = SelectPrimitive.GroupLabel.Props;
export function SelectLabel(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("px-3 py-2.5 text-xs text-muted-foreground", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <SelectPrimitive.GroupLabel data-slot="select-label" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type SelectItemProps = SelectPrimitive.Item.Props;
export function SelectItem(props) {
  const $ = _c(13);
  let children;
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== className) {
    t0 = cn("relative flex w-full cursor-default items-center gap-2.5 rounded-xl py-2 pr-8 pl-3 text-sm outline-hidden select-none", "focus:bg-accent focus:text-accent-foreground", "not-data-[variant=destructive]:focus:**:text-accent-foreground", "data-disabled:pointer-events-none data-disabled:opacity-50", "[&_svg]:pointer-events-none [&_svg]:shrink-0", "[&_svg:not([class*='size-'])]:size-4", "*:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2", className);
    $[4] = className;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== children) {
    t1 = <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">{children}</SelectPrimitive.ItemText>;
    $[6] = children;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectPrimitive.ItemIndicator render={<span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />}><CheckIcon className="pointer-events-none" /></SelectPrimitive.ItemIndicator>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== rest || $[10] !== t0 || $[11] !== t1) {
    t3 = <SelectPrimitive.Item data-slot="select-item" className={t0} {...rest}>{t1}{t2}</SelectPrimitive.Item>;
    $[9] = rest;
    $[10] = t0;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
type SelectSeparatorProps = SelectPrimitive.Separator.Props;
export function SelectSeparator(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("pointer-events-none -mx-1 my-1 h-px bg-border/50", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <SelectPrimitive.Separator data-slot="select-separator" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type SelectScrollUpButtonProps = React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>;
export function SelectScrollUpButton(props) {
  const $ = _c(9);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChevronUpIcon />;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== rest || $[7] !== t0) {
    t2 = <SelectPrimitive.ScrollUpArrow data-slot="select-scroll-up-button" className={t0} {...rest}>{t1}</SelectPrimitive.ScrollUpArrow>;
    $[6] = rest;
    $[7] = t0;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
type SelectScrollDownButtonProps = React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>;
export function SelectScrollDownButton(props) {
  const $ = _c(9);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1", "[&_svg:not([class*='size-'])]:size-4", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChevronDownIcon />;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== rest || $[7] !== t0) {
    t2 = <SelectPrimitive.ScrollDownArrow data-slot="select-scroll-down-button" className={t0} {...rest}>{t1}</SelectPrimitive.ScrollDownArrow>;
    $[6] = rest;
    $[7] = t0;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}